import React, { useState, useRef } from 'react';
import { FaTimes, FaCloudUploadAlt, FaFileExcel, FaSpinner, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import * as xlsx from 'xlsx';

interface ImportUsersProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportResult {
  success: number;
  failed: { row: number; reason: string }[];
}

export const ImportUsersOffcanvas: React.FC<ImportUsersProps> = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const validateFile = (selectedFile: File) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(selectedFile.type)) {
      toast.error('Invalid file type. Please upload a CSV or Excel file.');
      return false;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit.');
      return false;
    }

    return true;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const downloadSampleTemplate = () => {
    // 1. Define the required headers
    const headers = [
      "first_name", "last_name", "email", "username", "password", 
      "employee_code", "role", "reporting_latitude", "reporting_longitude",
      "association_type", "status", "employment_status", "phone_number",
      "department_id", "designation_id", "gender"
    ];

    // 2. Create one row of dummy data as an example
    const dummyData = {
      first_name: "John",
      last_name: "Doe",
      email: "john.doe@example.com",
      username: "johndoe123",
      password: "TempPassword123!",
      employee_code: "EMP001",
      role: "Site Engineer", // Exact role name from system
      reporting_latitude: "28.6139",
      reporting_longitude: "77.2090",
      association_type: "inhouse", // inhouse or outsourced
      status: "initiated", // initiated, verified, pending, suspended, deactivated
      employment_status: "working", // working, not_working, left
      phone_number: "9876543210",
      department_id: "", // ID if known
      designation_id: "", // ID if known
      gender: "male" // male, female, other
    };

    // 3. Convert to worksheet
    const worksheet = xlsx.utils.json_to_sheet([dummyData], { header: headers });

    // Auto-size columns slightly for better reading
    const colWidths = headers.map(h => ({ wch: Math.max(h.length, 15) }));
    worksheet['!cols'] = colWidths;

    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Users Template");
    
    // 4. Download file
    xlsx.writeFile(workbook, "Eminence_Users_Import_Template.xlsx");
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setIsUploading(true);
    setResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('/users/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setResult(response.data.data);
        if (response.data.data.failed.length === 0) {
          toast.success('All users imported successfully!');
        } else {
          toast.success(`Import finished with ${response.data.data.failed.length} errors`);
        }
        onSuccess();
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end transition-opacity">
      <div className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 translate-x-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Import Users</h2>
            <p className="text-sm text-gray-500 mt-1">Upload an Excel or CSV file to create users in bulk</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {!result ? (
            <div className="space-y-6">
              {/* Info & Template Download */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Before you start:</h3>
                <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 mb-4">
                  <li>Maximum 500 rows per file</li>
                  <li>File size must be under 5MB</li>
                  <li>Passwords should be treated as temporary</li>
                  <li>Ensure role names exactly match the system roles</li>
                </ul>
                <button
                  onClick={downloadSampleTemplate}
                  className="flex items-center gap-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 px-4 py-2 rounded hover:bg-blue-50 transition-colors shadow-sm"
                >
                  <FaFileExcel className="text-green-600" />
                  Download Sample Template
                </button>
              </div>

              {/* Upload Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100">
                    <FaCloudUploadAlt className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  Click to upload or drag & drop
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  SVG, PNG, JPG or GIF (max. 800x400px)
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Excel (.xlsx) or CSV (.csv) up to 5MB
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-white border border-gray-300 rounded shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Browse Files
                </button>
              </div>

              {/* Selected File Indicator */}
              {file && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <FaFileExcel className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Results View */
            <div className="space-y-6">
              <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-200 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <FaCheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Import Complete</h3>
                <p className="text-gray-600">
                  Successfully imported <span className="font-bold text-green-600">{result.success}</span> users.
                </p>
              </div>

              {result.failed.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FaExclamationCircle className="text-red-500" />
                      Failed Rows ({result.failed.length})
                    </h4>
                  </div>
                  <div className="border border-red-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-red-50 text-red-900">
                        <tr>
                          <th className="px-4 py-3 font-semibold w-24">Row</th>
                          <th className="px-4 py-3 font-semibold">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100 bg-white">
                        {result.failed.map((fail, idx) => (
                          <tr key={idx} className="hover:bg-red-50/50">
                            <td className="px-4 py-3 font-medium text-red-900">#{fail.row}</td>
                            <td className="px-4 py-3 text-red-700">{fail.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          
          {!result && (
            <button
              onClick={handleUpload}
              disabled={!file || isUploading}
              className="px-6 py-2 bg-blue-800 text-white rounded shadow-sm text-sm font-medium hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <FaSpinner className="animate-spin w-4 h-4" />
                  Importing...
                </>
              ) : (
                'Upload & Import'
              )}
            </button>
          )}

          {result && (
            <button
              onClick={() => {
                setResult(null);
                setFile(null);
              }}
              className="px-6 py-2 bg-blue-800 text-white rounded shadow-sm text-sm font-medium hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-800"
            >
              Import Another File
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
